import React, { useMemo, useRef } from 'react';
import { SelectField } from '@/components/form/fields/SelectField';
import { IModuleSchema, ModuleService, moduleSchema } from '@/api/module';
import { IProjectSchema } from '@/api/project';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { InputField } from '@/components/form/fields/InputField';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog, Modal, ModalRef } from 'alurkerja-ui';
import { useForm } from 'react-hook-form';
import { CreateButtonModal } from '@/components/shared/button/CreateButton';
import { SelectTreeField } from '@/components/form/fields/SelectTreeField';
import { UserService, IUserSchema } from '@/api/user';

type ModuleProps = {
  project: IProjectSchema;
  onSuccess: () => void;
};

interface CreateModuleProps extends ModuleProps {
  triggerButton?: React.ReactNode;
}

interface ModalContentProps extends ModuleProps {
  closeModal?: () => void;
}

const ModalContent: React.FC<ModalContentProps> = ({
  closeModal,
  project,
  onSuccess,
}) => {
  const { data: modules } = ModuleService.useGetModulesOption(project.id);

  const { data: usersData } = UserService.useGetAllUser();

  const userOptions = useMemo(() => {
    return (
      usersData?.data?.content?.map((user: IUserSchema) => ({
        label:
          `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
          user.email,
        value: user.id,
      })) ?? []
    );
  }, [usersData]);

  const { mutate: createModule, isPending: isLoading } =
    ModuleService.useCreate();

  const form = useForm<IModuleSchema>({
    resolver: zodResolver(moduleSchema),
  });

  const submitAction = (data: IModuleSchema) => {
    data.project_id = project.id;

    const onSuccessCallback = () => {
      Dialog.success({
        title: 'Success',
        description: data.parent_id
          ? 'Sub Module has been created'
          : 'Module has been created',
        callback: () => {
          onSuccess();
          closeModal?.();
        },
      });
    };

    createModule(data, { onSuccess: onSuccessCallback });
  };

  return (
    <FormContainer form={form} onSubmit={submitAction}>
      <SelectTreeField
        name="parent_id"
        label="Select Epic"
        placeholder="Please Select A Epic"
        options={modules}
      />

      <InputField
        name="module_name"
        label="User Story Name"
        placeholder="Please Enter User Story Name"
        hasRequiredAsterisk
      />

      <SelectField
        name="pic_id"
        label="PIC"
        placeholder="Select PIC"
        options={userOptions}
      />

      <FormFooter>
        <OutlineButton onClick={closeModal}>Cancel</OutlineButton>
        <Button>Save & Continue</Button>
        <Button loading={isLoading}>Save</Button>
      </FormFooter>
    </FormContainer>
  );
};

export const AddModuleOrSubmodule: React.FC<CreateModuleProps> = props => {
  const ref = useRef<ModalRef>(null);

  return (
    <Modal
      ref={ref}
      title="Add Epic / Add User Story"
      triggerButton={
        props.triggerButton ?? <CreateButtonModal label="Add Epic" />
      }
    >
      <div className="p-4">
        <ModalContent closeModal={ref.current?.closeModal} {...props} />
      </div>
    </Modal>
  );
};
